/**
 * Video export progress, expressed as the existing ReelProgress so the reel
 * progress UI and copy are reused as-is. Each stage owns a band of the bar:
 * prepare 0–8, render 8–90, encode 90–95, mux 95–99, ready 100.
 */

import { reelProgressAt, type ReelProgress, type ReelStage } from '@/lib/reelProgress';

export type VideoExportStage = Extract<ReelStage, 'prepare' | 'render' | 'encode' | 'mux' | 'ready' | 'failed'>;

export const VIDEO_PROGRESS_BANDS: Record<'prepare' | 'render' | 'encode' | 'mux', [number, number]> = {
  prepare: [0, 8],
  render: [8, 90],
  encode: [90, 95],
  mux: [95, 99],
};

/** Frames measured before an ETA is shown; earlier frames include scene loading. */
export const ETA_WARMUP_FRAMES = 30;

/** Percent for `fraction` (0..1) through a stage's band. */
export function bandPercent(stage: keyof typeof VIDEO_PROGRESS_BANDS, fraction: number): number {
  const [lo, hi] = VIDEO_PROGRESS_BANDS[stage];
  const f = Math.min(1, Math.max(0, fraction));
  return Math.round((lo + (hi - lo) * f) * 10) / 10;
}

/** "About 12 s left" from measured ms/frame, or undefined during warm-up. */
export function etaHint(framesDone: number, framesTotal: number, elapsedMs: number): string | undefined {
  if (framesDone < ETA_WARMUP_FRAMES || framesDone <= 0) return undefined;
  const leftMs = (elapsedMs / framesDone) * Math.max(0, framesTotal - framesDone);
  const sec = Math.ceil(leftMs / 1000);
  if (sec <= 1) return 'Almost done';
  if (sec < 60) return `About ${sec} s left`;
  return `About ${Math.ceil(sec / 60)} min left`;
}

export function videoProgressAt(
  stage: VideoExportStage,
  extras?: Partial<ReelProgress>
): ReelProgress {
  return reelProgressAt(stage, { mode: 'video', ...extras });
}
